import { AdminIcon } from '../components/AdminIcon';
import { StatusBadge } from '../components/AppointmentList';
import { Modal } from '../components/Modal';
import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { searchAppointmentByCode, getClinicConfig, getPublicAvailability, manageAppointment } from '../services/storageService';
import { Appointment, ClinicConfig, Attendance } from '../types';
import { maskPhone } from '../utils/maskUtils';
import { formatDateBr, generateTimeSlots } from '../utils/dateUtils';
import { attendanceLabel, getAvailableAttendance, todaySP } from '../utils/schedulingRules';

interface PublicTrackingProps {
  isEmbed?: boolean;
}

const EmbedTrackingWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-transparent font-sans text-primary px-4 py-5 md:px-6 md:py-6">
    {children}
  </div>
);

export const PublicTracking: React.FC<PublicTrackingProps> = ({ isEmbed = false }) => {
  const [details, setDetails] = useState<Partial<Appointment> | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appointments, setAppointments] = useState<Partial<Appointment>[]>([]);
  const [management, setManagement] = useState<{ appointment: Partial<Appointment>; action: 'cancel' | 'reschedule' } | null>(null);
  const [config, setConfig] = useState<ClinicConfig | null>(null);
  const [availability, setAvailability] = useState<Appointment[]>([]);
  const [phone, setPhone] = useState('');
  const [target, setTarget] = useState<{ date: string; time: string; attendance: Attendance | '' }>({ date: '', time: '', attendance: '' });
  const [busy, setBusy] = useState(false);
  const [managementError, setManagementError] = useState('');
  const [notice, setNotice] = useState('');

  const openManagement = async (appointment: Partial<Appointment>, action: 'cancel' | 'reschedule') => {
    setError(''); setBusy(true); setPhone(''); setTarget({ date: '', time: '', attendance: '' }); setManagementError('');
    try {
      const [cfg, slots] = await Promise.all([getClinicConfig(), getPublicAvailability()]);
      setConfig(cfg); setAvailability(slots); setManagement({ appointment, action });
    } catch { setError('Não foi possível carregar as opções. Tente novamente.'); }
    finally { setBusy(false); }
  };

  const submitManagement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!management?.appointment.code || busy) return;
    setBusy(true); setManagementError('');
    try {
      const message = await manageAppointment(management.appointment.code, phone.replace(/\D/g, ''), management.action, management.action === 'reschedule' && target.attendance ? { ...target, attendance: target.attendance } : undefined);
      setNotice(message); setManagement(null);
      const data = await searchAppointmentByCode(query.trim()); setAppointments(data || []);
    } catch (err) { setManagementError((err as Error).message); }
    finally { setBusy(false); }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query) return;

    setLoading(true);
    setError('');
    setAppointments([]);

    try {
      const data = await searchAppointmentByCode(query.trim());
      if (data && data.length > 0) {
        setAppointments(data);
      } else {
        setError('Nenhum agendamento futuro encontrado com estes dados.');
      }
    } catch (err) {
      setError('Erro ao buscar agendamento. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();

    // Check if user is typing numbers (phone attempt)
    const isNumeric = /^[0-9()\-\s]*$/.test(value);

    if (isNumeric && value.length > 0) {
      // Apply mask
      value = maskPhone(value);
    }

    setQuery(value);
  };

  const renderAppointmentCard = (appt: Partial<Appointment>) => <div className="public-session-row">
    <div className="min-w-0 flex-1"><h3 className="text-sm font-medium text-primary truncate">{appt.clientName}</h3><p className="text-xs text-gray-500 mt-1">{appt.date ? formatDateBr(appt.date) : '-'} · {appt.time} · {attendanceLabel(appt.attendance)}</p></div>
    <StatusBadge status={appt.status || 'pending'} /><button className="list-icon-button" title="Ver detalhes" aria-label={'Ver detalhes da sessão de ' + (appt.date ? formatDateBr(appt.date) : '')} onClick={() => setDetails(appt)}><AdminIcon name="eye" /></button>
  </div>;

  const Wrapper = isEmbed ? EmbedTrackingWrapper : Layout;

  return (
    <Wrapper>
      {details && <Modal title="Detalhes da sessão" busy={busy} onClose={() => setDetails(null)} footer={<><Button variant="secondary" disabled={busy} onClick={() => setDetails(null)}>Fechar</Button>{details.status !== 'cancelled' && <Button disabled={busy} onClick={async () => { await openManagement(details, 'reschedule'); setDetails(null); }}>Solicitar remarcação</Button>}</>}>
        <div className="session-detail-patient"><div><span className="modal-field-label">Paciente</span><h4>{details.clientName}</h4></div><StatusBadge status={details.status || 'pending'} /></div>
        <dl className="session-detail-grid"><div><dt>Data</dt><dd>{details.date ? formatDateBr(details.date) : '-'}</dd></div><div><dt>Horário</dt><dd>{details.time}</dd></div><div><dt>Atendimento</dt><dd>{attendanceLabel(details.attendance)}</dd></div><div><dt>Código</dt><dd>{details.code || '-'}</dd></div></dl>
        {details.status === 'pending' && <p className="text-sm text-amber-700 mt-4">Aguardando confirmação da profissional.</p>}
        {details.status !== 'cancelled' && <button className="session-cancel-button mt-4 px-4" disabled={busy} onClick={async () => { await openManagement(details, 'cancel'); setDetails(null); }}>Cancelar sessão</button>}
      </Modal>}

      {management && config && <Modal title={management.action === 'cancel' ? 'Cancelar sessão' : 'Solicitar remarcação'} busy={busy} onClose={() => setManagement(null)} footer={<><Button disabled={busy} variant="secondary" onClick={() => setManagement(null)}>Voltar</Button><Button type="submit" form="manage-session" variant={management.action === 'cancel' ? 'danger' : 'primary'} disabled={busy}>{busy ? 'Enviando...' : management.action === 'cancel' ? 'Confirmar cancelamento' : 'Enviar pedido'}</Button></>}><form id="manage-session" onSubmit={submitManagement} className="space-y-4">
        <p className="text-sm text-gray-600">{formatDateBr(management.appointment.date || '')} às {management.appointment.time}</p>
        <p className="text-xs text-gray-500">Alterações disponíveis até {config.cancellationHours} horas antes da sessão.</p>
        <Input label="Confirme o WhatsApp cadastrado" type="tel" required value={phone} maxLength={15} onChange={e => setPhone(maskPhone(e.target.value))} />
        {management.action === 'reschedule' && <>
          <Input label="Nova data" type="date" required min={todaySP()} value={target.date} onChange={e => setTarget({ date: e.target.value, time: '', attendance: '' })} />
          {target.date && <label className="block text-sm">Atendimento<select aria-label="Atendimento" required className="w-full border rounded p-2 mt-1" value={target.attendance} onChange={e => setTarget({ ...target, attendance: e.target.value as Attendance, time: '' })}><option value="">Selecione</option>{getAvailableAttendance(target.date, config).map(value => <option key={value} value={value}>{attendanceLabel(value)}</option>)}</select></label>}
          {target.attendance && <label className="block text-sm">Novo horário<select aria-label="Novo horário" required className="w-full border rounded p-2 mt-1" value={target.time} onChange={e => setTarget({ ...target, time: e.target.value })}><option value="">Selecione</option>{generateTimeSlots(target.date, config, availability).map(slot => <option key={slot.time} value={slot.time}>{slot.time}</option>)}</select></label>}
          <p className="text-xs text-gray-500">A sessão original permanece reservada. O novo horário depende de disponibilidade e aprovação da profissional.</p>
        </>}
        {management.action === 'cancel' && <p className="text-sm text-red-700">Ao confirmar, sua sessão será cancelada e o horário será liberado.</p>}
        {managementError && <p role="alert" className="text-sm text-red-700">{managementError}</p>}

      </form></Modal>}
      <div className={`max-w-2xl mx-auto animate-fade-in ${isEmbed ? 'py-2' : 'py-8 md:py-12 px-4'}`}>
        {notice && <p role="status" className="bg-green-50 text-green-800 p-4 rounded-lg mb-4 text-sm">{notice}</p>}
        <div className={`text-center ${isEmbed ? 'mb-5' : 'mb-8 md:mb-12'}`}>
          <h2 className={`${isEmbed ? 'text-2xl' : 'text-3xl md:text-4xl'} font-serif text-primary font-bold mb-3`}>
            Sua Agenda
          </h2>
          <p className="text-gray-500 max-w-md mx-auto text-sm md:text-base">Consulte status e detalhes dos seus agendamentos usando seu Código ou Celular.</p>
        </div>

        {isEmbed && (
          <div className="mb-5">
            <a
              href="#/embed"
              className="inline-flex items-center justify-center w-full rounded-md border border-primary/15 bg-white px-4 py-3 text-sm font-medium text-primary shadow-sm transition-colors hover:bg-primary/5"
            >
              Voltar para agendamento
            </a>
          </div>
        )}

        <div className={`bg-white rounded-xl shadow-lg border border-primary/5 p-5 md:p-6 relative overflow-hidden ${isEmbed ? 'mb-6' : 'mb-10'}`}>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary opacity-20"></div>
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Input
                label="Código ou Celular"
                placeholder="Ex: (11) 99999-9999"
                value={query}
                onChange={handleInputChange}
                className="text-center font-mono tracking-widest text-lg uppercase h-12"
                maxLength={15}
              />
            </div>
            <div className="w-full md:w-auto mb-1">
              <Button type="submit" variant="primary" disabled={loading || !query} className="h-12 w-full md:w-auto px-8 shadow-md">
                {loading ? 'Buscando...' : 'Consultar'}
              </Button>
            </div>
          </form>

          {error && (
            <div className="mt-6 p-4 bg-red-50 text-red-700 rounded-xl text-sm border border-red-100 flex items-center gap-3 animate-shake">
              <span className="text-xl">⚠️</span> {error}
            </div>
          )}
        </div>

        {appointments.length > 0 && (
          <div className="animate-slide-up">
            {appointments.length > 1 && (
              <div className="flex items-center gap-3 mb-6 ml-1">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                  {appointments.length}
                </div>
                <h3 className="text-xl font-serif font-bold text-primary">
                  Agendamentos Encontrados
                </h3>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              {appointments.map((appt, idx) => (
                <div key={idx} className="w-full">
                  {renderAppointmentCard(appt)}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </Wrapper>
  );
};
